import React, { useEffect, useState } from 'react';
import { Button, Form, notification } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import DbParametersModal from './DbParametersModal';
import styles from './EditDbParametersModal.module.less';
import DbParameterGroupService from '../../services/DbParameterGroupService';
import CcxIconCheckCircleTwoTone from '../ccx/icons/CcxIconCheckCircleTwoTone';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import { wrapDbParameterValues } from '../../core/helpers';
interface EditDBParametersModalProps {
    record: any;
    refresh?: () => void;
    isEditVisible: boolean;
    setIsEditVisible: (value: boolean) => void;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditDBParametersModal = ({
    record,
    refresh,
    setIsLoading,
    isEditVisible,
    setIsEditVisible,
}: EditDBParametersModalProps) => {
    const [visible, setVisible] = useState<boolean>(false);
    const [editRecord, setEditRecord] = useState<any>(record);
    const [form] = Form.useForm();
    const [tableForm] = Form.useForm();

    useEffect(() => {
        if (isEditVisible) {
            setIsLoading(true);
            handleOpenDbParametersModal();
        }
    }, [isEditVisible, record]);

    useEffect(() => {
        if (!visible) {
            setIsEditVisible(false);
        }
    }, [visible]);

    const handleOpenDbParametersModal = async () => {
        const defaultParameters =
            await DbParameterGroupService.getDefaultDbParameterGroup(
                record.databaseVendor
            );
        const mappedParameters = wrapDbParameterValues(
            record.dbParameters,
            defaultParameters.getAllParameters()
        );

        setEditRecord({
            ...record,
            dbParameters: mappedParameters,
        });
        setVisible(isEditVisible);
        setIsLoading(false);
    };

    const onSubmit = async (formData: any) => {
        const data: any = {
            name: formData.name,
            database_vendor: formData.databaseVendor,
            database_version: formData.databaseVersion,
            database_type: formData.configuration,
            description: formData.description,
            parameters: formData.dbParameters,
            data_stores: formData.dataStores,
        };
        try {
            await DbParameterGroupService.updateDbParameterGroup(
                record.uuid,
                data
            );
            notification.open({
                message: 'DB Parameter Group Updated',
                description:
                    'Database parameter group has been updated successfully.',
                icon: <CcxIconCheckCircleTwoTone twoToneColor="#52c41a" />,
            });
            if (refresh) {
                refresh();
            }
            setIsEditVisible(false);
            form.resetFields();
            tableForm.resetFields();
        } catch (error: any) {
            const errorMessage =
                error instanceof Error || error.message
                    ? error.message
                    : String(error);
            validateFields(errorMessage, error);

            notification.open({
                message: 'Failed to Update DB Parameter Group',
                description: `An error occurred while updating the database parameter group: ${errorMessage}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });
            console.error(error);
        }
    };

    const validateFields = (errorMessage: string, error: any) => {
        const isNameUnique = errorMessage.includes(
            form.getFieldValue('groupName')
        );

        if (isNameUnique) {
            form.setFields([
                {
                    name: 'groupName',
                    errors: ['Parameter group with this name already exists'],
                },
            ]);
        }

        if (error?.errorFields?.length > 0) {
            error.errorFields.forEach(
                (field: { name: string; description: string }) => {
                    tableForm.setFields([
                        {
                            name: field.name,
                            errors: ['Enter valid parameters'],
                        },
                    ]);
                }
            );
        }
    };

    const handleCancel = () => {
        setVisible(false);
    };

    return (
        <>
            <Button
                type="link"
                className={styles.EditDbParametersPopoverButton}
                icon={<EditOutlined />}
                onClick={handleOpenDbParametersModal}
            >
                Edit
            </Button>
            <DbParametersModal
                visible={visible}
                onSubmit={onSubmit}
                setVisible={setVisible}
                handleCancel={handleCancel}
                operation="edit"
                record={editRecord}
                form={form}
                tableForm={tableForm}
                disableFields={true}
            />
        </>
    );
};

export default EditDBParametersModal;
